"use client";

import { useState, useRef, useEffect } from "react";
import type { FaqBlock } from "@/lib/content";

function ResourceRow({ item }: { item: any }) {
  return (
    <a
      href={item.href}
      target={item.href.startsWith("http") ? "_blank" : undefined}
      rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
      className="flex items-center justify-between gap-4 border-b border-line py-3 text-[15px] transition-colors hover:text-brass-deep"
    >
      <span className="font-medium">{item.label}</span>
      <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-ink-60">
        {item.kind === "pdf" ? "PDF" : "Link"} ↗
      </span>
    </a>
  );
}

export function FaqItem({ item }: { item: FaqBlock["items"][number] }) {
  const [open, setOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(open ? undefined : 0);

  useEffect(() => {
    if (open) {
      const el = contentRef.current;
      if (el) {
        setHeight(el.scrollHeight);
      }
    } else {
      setHeight(0);
    }
  }, [open]);

  return (
    <div className="group border-b border-line">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex cursor-pointer list-none items-center justify-between gap-4 py-5 text-left outline-none"
      >
        <span className="font-display text-[19px] font-semibold text-ink">
          {item.question}
        </span>
        <span
          className={`font-mono text-brass-deep transition-transform duration-300 ${
            open ? "rotate-45" : ""
          }`}
        >
          +
        </span>
      </button>
      <div
        className="overflow-hidden transition-[height] duration-300 ease-in-out"
        style={{ height }}
      >
        <div ref={contentRef} className="pb-6">
          <div
            className="prose-editorial max-w-prose2 text-[16px] leading-[1.7] text-ink-60"
            dangerouslySetInnerHTML={{ __html: item.answerHtml }}
          />
          {item.resources && item.resources.length > 0 && (
            <div className="mt-5 max-w-prose2">
              {item.resources.map((r, ri) => (
                <ResourceRow key={`${r.href}-${r.label}-${ri}`} item={r} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
